import React, { useMemo } from "react";
import styled from "styled-components";

// Modern color palette - widely used by Google, Slack, Microsoft, etc.
const AVATAR_COLORS = [
  "#F44336", // Red
  "#E91E63", // Pink
  "#9C27B0", // Purple
  "#673AB7", // Deep Purple
  "#3F51B5", // Indigo
  "#2196F3", // Blue
  "#03A9F4", // Light Blue
  "#00BCD4", // Cyan
  "#009688", // Teal
  "#4CAF50", // Green
  "#8BC34A", // Light Green
  "#FF9800", // Orange
  "#FF5722", // Deep Orange
  "#795548", // Brown
  "#607D8B", // Blue Grey
];

/**
 * Generate a consistent color based on name
 * Same name will always get the same color
 */
const getColorFromName = (name) => {
  if (!name) return AVATAR_COLORS[0];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

/**
 * Get the first letter of the name (uppercase)
 */
const getInitial = (name) => {
  if (!name || typeof name !== "string") return "?";
  const trimmed = name.trim();
  return trimmed.charAt(0).toUpperCase();
};

const AvatarWrapper = styled.div`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  min-width: ${({ size }) => size}px;
  min-height: ${({ size }) => size}px;
  border-radius: 50%;
  background-color: ${({ $bgColor }) => $bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 600;
  font-size: ${({ size }) => Math.round(size * 0.45)}px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  user-select: none;
  flex-shrink: 0;
`;

/**
 * InitialsAvatar Component
 * Displays a circular avatar with the first letter of the user's name
 * Color is consistently generated based on the name
 *
 * @param {string} name - User's name (first letter will be used)
 * @param {number} size - Avatar size in pixels (default: 40)
 * @param {string} className - Optional CSS class
 * @param {object} style - Optional inline styles
 */
const InitialsAvatar = ({ name, size = 40, className, style }) => {
  const initial = useMemo(() => getInitial(name), [name]);
  const backgroundColor = useMemo(() => getColorFromName(name), [name]);

  return (
    <AvatarWrapper
      size={size}
      $bgColor={backgroundColor}
      className={className}
      style={style}
      title={name}
    >
      {initial}
    </AvatarWrapper>
  );
};

export default InitialsAvatar;
